import React, { useEffect, useState } from 'react';
import './styles/reqModal.css';
import {
	RavenButton,
	RavenInputField,
	RavenModal,
	RavenTextTrim,
	RavenToolTip,
} from '@ravenpay/raven-bank-ui';
import { useDispatch, useSelector } from 'react-redux';
import RootState, {
	SettlementChangeRequest,
} from '../../../../../../../redux/types';
import { respondToSettlementRequest } from '../../../../../../../redux/settlement';
import { bankboxAPI } from '../../../../../../../redux/bankbox';
import CheckUrlFragment from '../../../../../../../components/common/CheckUrlCodeFragment';
import ravenlogo from '../../../../../../../assets/raven-small-logo-green.svg';

type ModalStep = 'preview' | 'reject' | 'accept';

const AccountRow = ({
	label,
	value,
	icon,
	isnewAccount,
	noBorder,
}: {
	label: string;
	value: React.ReactNode;
	icon?: React.ReactNode;
	isOldAccount?: boolean;
	isnewAccount?: boolean;
	noBorder?: boolean;
}) => (
	<div
		style={{
			display: 'flex',
			alignItems: 'center',
			padding: '10px 0',
			borderBottom: noBorder ? 'none' : '1px solid #eee',
		}}
	>
		<span
			style={{
				color: '#999',
				fontSize: '1.4rem',
				width: '45%',
			}}
		>
			{label}
		</span>
		<span
			style={{
				fontWeight: 500,
				fontSize: '1.2rem',
				display: 'flex',
				alignItems: 'center',
				flex: 1,
				gap: isnewAccount ? '1rem' : '1rem',
			}}
		>
			{icon}
			{value}
		</span>
	</div>
);

const bankIcon = (
	<svg
		className="img"
		width="36"
		height="36"
		viewBox="0 0 36 36"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M30.1895 14.9425C30.189 14.6126 30.1107 14.2874 29.961 13.9933C29.8114 13.6993 29.5945 13.4446 29.328 13.25L19.622 6.16453C19.2682 5.90658 18.8426 5.76569 18.4047 5.76157C17.9669 5.75744 17.5387 5.89029 17.18 6.14153L7.04452 13.25C6.76854 13.4435 6.54318 13.7006 6.38749 13.9995C6.2318 14.2985 6.15035 14.6305 6.15002 14.9675C6.14996 15.0782 6.1717 15.1878 6.21401 15.2901C6.25632 15.3924 6.31837 15.4853 6.39661 15.5636C6.47485 15.6419 6.56775 15.704 6.67 15.7464C6.77225 15.7887 6.88184 15.8105 6.99252 15.8105H29.322C29.436 15.8105 29.5488 15.7881 29.6541 15.7445C29.7594 15.7008 29.8551 15.6369 29.9356 15.5563C30.0162 15.4757 30.0801 15.38 30.1236 15.2747C30.1672 15.1694 30.1896 15.0565 30.1895 14.9425Z"
			fill="var(--primary-base-color)"
		/>
		<path
			d="M6.15002 27.1804V28.2804C6.15002 28.8374 6.37127 29.3715 6.7651 29.7653C7.15893 30.1592 7.69307 30.3804 8.25002 30.3804H28.0915C28.6485 30.3804 29.1826 30.1592 29.5764 29.7653C29.9703 29.3715 30.1915 28.8374 30.1915 28.2804V27.1804H6.15002Z"
			fill="var(--primary-base-color)"
		/>
		<path
			d="M18.6415 16.6289H17.6995C17.0017 16.6289 16.436 17.1946 16.436 17.8924V25.0569C16.436 25.7547 17.0017 26.3204 17.6995 26.3204H18.6415C19.3393 26.3204 19.905 25.7547 19.905 25.0569V17.8924C19.905 17.1946 19.3393 16.6289 18.6415 16.6289Z"
			fill="var(--primary-base-color)"
		/>
		<path
			d="M25.5335 16.6289H24.5915C23.8937 16.6289 23.328 17.1946 23.328 17.8924V25.0569C23.328 25.7547 23.8937 26.3204 24.5915 26.3204H25.5335C26.2313 26.3204 26.797 25.7547 26.797 25.0569V17.8924C26.797 17.1946 26.2313 16.6289 25.5335 16.6289Z"
			fill="var(--primary-base-color)"
		/>
		<path
			d="M11.7494 16.6289H10.8074C10.1096 16.6289 9.54395 17.1946 9.54395 17.8924V25.0569C9.54395 25.7547 10.1096 26.3204 10.8074 26.3204H11.7494C12.4473 26.3204 13.0129 25.7547 13.0129 25.0569V17.8924C13.0129 17.1946 12.4473 16.6289 11.7494 16.6289Z"
			fill="var(--primary-base-color)"
		/>
	</svg>
);

const AccountCard = ({
	accountNumber,
	bankName,
	accountName,
	bankLogo,
	isOldAccount,
	isnewAccount,
	bankCode,
}: {
	accountNumber: string;
	bankName: string;
	accountName: string;
	bankLogo?: string;
	bankCode?: string;
	isOldAccount?: boolean;
	isnewAccount?: boolean;
}) => (
	<div
		style={{
			borderRadius: '12px',
			border: '.1rem solid rgba(172, 172, 172, 1)',
			backgroundColor: '#fff',
			padding: '1.6rem',
			marginBottom: '2rem',
		}}
	>
		<AccountRow
			label="Account No."
			value={accountNumber}
			isOldAccount={isOldAccount}
			isnewAccount={isnewAccount}
		/>
		<AccountRow
			label="Bank"
			value={RavenTextTrim(bankName, 23)}
			icon={
				bankLogo && (
					<CheckUrlFragment
						className="img-box-general-size border-theme"
						url={`${bankLogo}`}
					>
						{bankCode === '090615' ? (
							<figure className="img-box-general-size">
								<img src={ravenlogo} alt="" className="img" />
							</figure>
						) : (
							<figure className="img-box-general-size">{bankIcon}</figure>
						)}
					</CheckUrlFragment>
				)
			}
			isOldAccount={isOldAccount}
			isnewAccount={isnewAccount}
		/>
		<AccountRow
			label="Account Name"
			value={
				<div
					onClick={() => {
						// console.log(bankLogo);
					}}
					className={accountName?.length >= 22 ? 'tooltip-hover-wrap' : ''}
				>
					<RavenToolTip
						textColor="white-light"
						color="deep-green-light"
						position="top-center"
						text={accountName}
					/>
					<span>{RavenTextTrim(accountName, 22)}</span>
				</div>
			}
			isOldAccount={isOldAccount}
			isnewAccount={isnewAccount}
			noBorder
		/>
	</div>
);

const ReqAccountModal = ({
	isOpen,
	onClose,
	onAction,
	onFinish,
	requestData,
}: {
	isOpen: boolean;
	onClose: () => void;
	onAction: () => void;
	onFinish?: () => void;
	requestData: SettlementChangeRequest | null;
}) => {
	const [modalStep, setModalStep] = useState<ModalStep>('preview');
	const [rejectReason, setRejectReason] = useState('');
	const dispatch = useDispatch();

	const { loading } = useSelector((state: RootState) => state.settlement);
	const { settlement_detail_req, user_merchant } = useSelector(
		(state: RootState) => state.bankbox
	);
	const [isRefreshing, setIsRefreshing] = useState(false);

	const childToUse = requestData || settlement_detail_req;
	useEffect(() => {
		if (!isOpen) {
			setModalStep('preview');
			setRejectReason('');
		}
	}, [isOpen]);

	const handleRespond = async (
		status: 'approved' | 'rejected',
		reason = ''
	) => {
		if (!requestData?.id || !requestData?.email) return;

		const payload = {
			id: requestData.id,
			email: requestData.email,
			status,
			note: reason,
		};

		try {
			setIsRefreshing(true);
			const data = await dispatch(respondToSettlementRequest(payload));
			if (data?.payload?.status === 'success') {
				setIsRefreshing(false);
				onFinish?.();
				onClose();
			} else {
				setIsRefreshing(false);
			}
		} catch (e) {
		} finally {
			setIsRefreshing(false);
		}
	};

	const renderHeader = () =>
		({
			preview: {
				title: 'Settlement Details Change Request',
				subtitle: 'Preview and Accept Request',
			},
			reject: {
				title: 'Reject Request',
				subtitle: 'Kindly give a reason for rejecting this request',
			},
			accept: {
				title: 'Accept Request',
				subtitle: 'Confirm to Accept the Change',
			},
		}[modalStep]);

	const renderActions = () => {
		switch (modalStep) {
			case 'preview':
				return (
					<div style={{ display: 'flex', gap: '2rem', marginTop: '2.5rem' }}>
						<RavenButton
							color="blue-light"
							label="Reject Request"
							onClick={() => setModalStep('reject')}
							style={{ width: '100%' }}
							className="reusable-btn-outline-classname-cancel"
						/>
						<RavenButton
							color="deep-green-dark"
							label="Accept Request"
							onClick={() => setModalStep('accept')}
							loading={loading}
							style={{ width: '100%' }}
						/>
					</div>
				);
			case 'reject':
				return (
					<div style={{ display: 'flex', gap: '2rem' }}>
						<RavenButton
							color="deep-green-dark"
							label="No, Cancel"
							className="reusable-btn-outline-classname-cancel"
							onClick={() => setModalStep('preview')}
							style={{ width: '100%', marginTop: '4rem' }}
						/>
						<RavenButton
							color="deep-green-light"
							label="Reject Request"
							loading={loading}
							disabled={!rejectReason}
							onClick={() => handleRespond('rejected', rejectReason)}
							style={{ width: '100%', marginTop: '4rem' }}
						/>
					</div>
				);
			case 'accept':
				return (
					<div style={{ display: 'flex', gap: '2rem' }}>
						<RavenButton
							color="deep-green-dark"
							label="No, Cancel"
							className="reusable-btn-outline-classname-cancel"
							onClick={() => setModalStep('preview')}
							style={{ width: '100%', marginTop: '4rem' }}
						/>
						<RavenButton
							color="deep-green-dark"
							label="Confirm Acceptance"
							onClick={() => handleRespond('approved')}
							loading={loading}
							style={{ width: '100%', marginTop: '4rem' }}
						/>
					</div>
				);
		}
	};

	const renderContent = () => {
		switch (modalStep) {
			case 'preview':
				return (
					<>
						<p style={{ ...labelStyles, color: 'rgba(139, 139, 139, 1)' }}>
							Old Account
						</p>
						<AccountCard
							accountNumber={
								requestData?.old_account_number ||
								user_merchant?.settlement_detail?.account_number
							}
							bankCode={
								requestData?.old_bank_code ||
								settlement_detail_req?.old_bank_code
							}
							accountName={
								requestData?.old_account_name ||
								user_merchant?.settlement_detail?.account_name
							}
							bankName={
								requestData?.old_bank || user_merchant?.settlement_detail?.bank
							}
							bankLogo={`https://businessapi.getraventest.com/static/media/banks/${
								requestData?.old_bank_code ||
								settlement_detail_req?.old_bank_code
							}.png`}
							isOldAccount
						/>

						<div style={{ borderTop: '1px dashed #ccc', margin: '2.4rem 0' }} />

						<p style={{ ...labelStyles, color: 'rgba(0, 0, 0, 1)' }}>
							New Account
						</p>
						<AccountCard
							accountNumber={
								requestData?.new_account_number ||
								settlement_detail_req?.new_account_number
							}
							bankCode={
								requestData?.new_bank_code ||
								settlement_detail_req?.new_bank_code
							}
							accountName={
								requestData?.new_account_name ||
								settlement_detail_req?.new_account_name
							}
							bankName={RavenTextTrim(
								requestData?.new_bank || settlement_detail_req?.new_bank,
								25
							)}
							bankLogo={`https://businessapi.getraventest.com/static/media/banks/${
								requestData?.new_bank_code ||
								settlement_detail_req?.new_bank_code
							}.png`}
							isnewAccount
						/>
					</>
				);
			case 'reject':
				return (
					<RavenInputField
						label="Reason for Rejection"
						color="deep-green-light"
						labelClassName="my_custom_class"
						value={rejectReason}
						placeholder="Write your reason here..."
						onChange={(e: any) => setRejectReason(e.target.value)}
						textareaColumn={30}
						textareaRow={5}
						type="textarea"
					/>
				);
			case 'accept':
				return null;
		}
	};

	if (!isOpen || !requestData) return null;

	const header = renderHeader();

	const labelStyles: React.CSSProperties = {
		color: 'rgba(0, 0, 0, 1)',
		fontWeight: '600',
		fontSize: '14px',
		marginBottom: '12px',
		letterSpacing: '0.02em',
		textTransform: 'uppercase',
	};

	return (
		<RavenModal
			className="trx"
			visble={isOpen}
			onBtnClick={onAction}
			onClose={() => {
				setModalStep('preview');
				onClose();
			}}
		>
			<div>
				<h2
					style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1rem' }}
				>
					{header.title}
				</h2>
				<p style={{ fontSize: '1.4rem', color: '#666', marginBottom: '2rem' }}>
					{header.subtitle}
				</p>
				{renderContent()}
				{String(user_merchant?.account_request?.status) === '0' ? renderActions() : ""}
			</div>
		</RavenModal>
	);
};

export default ReqAccountModal;
